<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { createLifecycleOperationUuid } from 'dashboard/helper/lifecycleOperationUuid';
import Button from 'dashboard/components-next/button/Button.vue';
import { HEADER_TEXT_CONTROL_CLASS } from 'dashboard/helper/conversationHeaderStyles';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import LifecycleStageBadge from './LifecycleStageBadge.vue';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], default: null },
  showHistory: { type: Boolean, default: true },
  compact: { type: Boolean, default: false },
  header: { type: Boolean, default: false },
});

const { t, locale } = useI18n();
const store = useStore();
const selectedStageId = ref('');
const reason = ref('');
const historyOpen = ref(false);
const isLoading = ref(false);
const historyState = ref(props.showHistory ? 'idle' : 'loaded');
const isLoadingMoreHistory = ref(false);
const loadMoreHistoryFailed = ref(false);
let historyRequestSequence = 0;

const settings = computed(
  () => store.getters['customerLifecycle/getSettings'] || {}
);
const stages = computed(
  () => store.getters['customerLifecycle/getActiveStages'] || []
);
const lifecycle = computed(
  () =>
    store.getters['customerLifecycle/getContactLifecycle'](props.contactId) ||
    null
);
const contact = computed(
  () => store.getters['contacts/getContact'](props.contactId) || {}
);
const contactStageId = computed(() => {
  if (contact.value.lifecycleStageId !== undefined) {
    return contact.value.lifecycleStageId;
  }
  return contact.value.lifecycle_stage_id;
});
const transitions = computed(
  () => store.getters['customerLifecycle/getTransitions'](props.contactId) || []
);
const transitionMeta = computed(
  () =>
    store.getters['customerLifecycle/getTransitionMeta'](props.contactId) || {}
);
const currentStageId = computed(() => {
  if (lifecycle.value) {
    return lifecycle.value.current_stage?.id ?? null;
  }
  if (contactStageId.value !== undefined) {
    return contactStageId.value ?? null;
  }
  return lifecycle.value?.current_stage?.id ?? null;
});
const currentStage = computed(() => {
  const fromActiveStages = stages.value.find(
    stage => String(stage.id) === String(currentStageId.value)
  );
  return fromActiveStages || lifecycle.value?.current_stage || null;
});
const defaultStage = computed(
  () =>
    stages.value.find(
      stage => stage.is_default && stage.category === 'active'
    ) || null
);
const defaultStageId = computed(() => defaultStage.value?.id ?? null);
const headerStageLabel = computed(() => {
  if (!currentStage.value) return t('TELETEL_LIFECYCLE.UNCLASSIFIED');
  const { emoji, name } = currentStage.value;
  return emoji ? `${emoji} ${name}` : name;
});
const isTransitioning = computed(() =>
  store.getters['customerLifecycle/isTransitioning'](props.contactId)
);
const isUsable = computed(
  () =>
    settings.value.available &&
    settings.value.enabled &&
    store.getters['customerLifecycle/areStagesLoaded']
);
const canLoadMoreHistory = computed(
  () =>
    historyState.value === 'loaded' &&
    Number(transitionMeta.value.current_page || 1) <
      Number(transitionMeta.value.total_pages || 1)
);

const resetStageSelection = () => {
  const selectedId =
    currentStageId.value === null && !props.header
      ? defaultStageId.value
      : currentStageId.value;
  selectedStageId.value = selectedId === null ? '' : String(selectedId);
};

watch([currentStageId, defaultStageId], resetStageSelection, {
  immediate: true,
});

const hasPendingStageChange = computed(
  () =>
    String(selectedStageId.value || '') !== String(currentStageId.value ?? '')
);

const loadInitialHistory = async () => {
  historyRequestSequence += 1;
  const requestSequence = historyRequestSequence;
  const contactId = props.contactId;
  historyState.value = 'loading';
  loadMoreHistoryFailed.value = false;
  try {
    await store.dispatch('customerLifecycle/fetchTransitions', {
      contactId,
      page: 1,
      perPage: 5,
    });
    if (
      requestSequence === historyRequestSequence &&
      String(contactId) === String(props.contactId)
    ) {
      historyState.value = 'loaded';
    }
  } catch {
    if (
      requestSequence === historyRequestSequence &&
      String(contactId) === String(props.contactId)
    ) {
      historyState.value = 'error';
    }
  }
};

const loadLifecycle = async () => {
  if (!props.contactId) return;
  isLoading.value = true;
  try {
    const loadedSettings = await store.dispatch(
      'customerLifecycle/getSettings'
    );
    if (!loadedSettings?.available || !loadedSettings?.enabled) return;
    await Promise.all([
      Promise.all([
        store.dispatch('customerLifecycle/getStages'),
        store.dispatch('customerLifecycle/fetchContact', props.contactId),
      ]),
      props.showHistory ? loadInitialHistory() : Promise.resolve(),
    ]);
  } catch {
    // The lifecycle endpoint is fail-closed. A missing/disabled feature should
    // not break the surrounding contact or conversation panel.
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.contactId,
  (value, oldValue) => {
    if (value && value !== oldValue) {
      historyRequestSequence += 1;
      historyState.value = props.showHistory ? 'idle' : 'loaded';
      isLoadingMoreHistory.value = false;
      loadMoreHistoryFailed.value = false;
      loadLifecycle();
    }
  }
);

onMounted(loadLifecycle);

const applyStage = async () => {
  const targetStageId = selectedStageId.value
    ? Number(selectedStageId.value)
    : null;
  if (String(targetStageId ?? '') === String(currentStageId.value ?? ''))
    return;

  try {
    const common = {
      contactId: props.contactId,
      expectedFromStageId: currentStageId.value,
      operationUuid: createLifecycleOperationUuid(),
      conversationId: props.conversationId,
      ...(reason.value.trim() ? { reason: reason.value.trim() } : {}),
    };
    if (targetStageId === null) {
      await store.dispatch('customerLifecycle/clearContact', common);
    } else {
      await store.dispatch('customerLifecycle/transitionContact', {
        ...common,
        toStageId: targetStageId,
      });
    }
    reason.value = '';
    useAlert(t('TELETEL_LIFECYCLE.CONTACT.SAVED'));
  } catch (error) {
    resetStageSelection();
    if (error?.response?.status === 409) {
      await store.dispatch('customerLifecycle/fetchContact', props.contactId);
      useAlert(t('TELETEL_LIFECYCLE.CONTACT.STALE'));
      return;
    }
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.CONTACT.SAVE_ERROR')
    );
  }
};

// A won/lost outcome picked from the header asks for an optional reason
// first (it feeds the loss report); active stages move immediately.
const pendingTerminal = ref(null);
const selectHeaderStage = async (stageId, hide) => {
  const stage = stages.value.find(item => String(item.id) === String(stageId));
  if (stage && ['won', 'lost'].includes(stage.category)) {
    pendingTerminal.value = stage;
    return;
  }
  selectedStageId.value = stageId === null ? '' : String(stageId);
  hide();
  await applyStage();
};
const confirmTerminal = async (hide, withReason = true) => {
  const stage = pendingTerminal.value;
  if (!stage) return;
  if (!withReason) reason.value = '';
  selectedStageId.value = String(stage.id);
  pendingTerminal.value = null;
  hide();
  await applyStage();
};
const cancelTerminal = () => {
  pendingTerminal.value = null;
  reason.value = '';
};

const loadMoreHistory = async () => {
  if (!canLoadMoreHistory.value || isLoadingMoreHistory.value) return;

  const requestSequence = historyRequestSequence;
  const contactId = props.contactId;
  isLoadingMoreHistory.value = true;
  loadMoreHistoryFailed.value = false;
  try {
    await store.dispatch('customerLifecycle/fetchTransitions', {
      contactId,
      page: Number(transitionMeta.value.current_page || 1) + 1,
      perPage: Number(transitionMeta.value.per_page || 5),
    });
  } catch {
    if (
      requestSequence === historyRequestSequence &&
      String(contactId) === String(props.contactId)
    ) {
      loadMoreHistoryFailed.value = true;
    }
  } finally {
    if (
      requestSequence === historyRequestSequence &&
      String(contactId) === String(props.contactId)
    ) {
      isLoadingMoreHistory.value = false;
    }
  }
};

const stageSnapshotName = snapshot => {
  if (!snapshot) return t('TELETEL_LIFECYCLE.UNCLASSIFIED');
  if (typeof snapshot === 'string') return snapshot;
  return snapshot.name || t('TELETEL_LIFECYCLE.UNCLASSIFIED');
};

const formatTime = value => {
  if (!value) return '—';
  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};
</script>

<template>
  <div
    v-if="isUsable && header"
    class="flex min-w-0 shrink items-center"
    :aria-label="t('TELETEL_LIFECYCLE.CONTACT.TITLE')"
  >
    <Popover align="start">
      <template #default="{ isOpen }">
        <Button
          type="button"
          ghost
          slate
          size="xs"
          :aria-label="t('TELETEL_LIFECYCLE.CONTACT.CHANGE_STAGE')"
          :aria-expanded="isOpen"
          class="max-w-44 min-w-16 px-2 text-xs font-medium"
          :class="[HEADER_TEXT_CONTROL_CLASS, { '!bg-n-alpha-2': isOpen }]"
        >
          <span class="min-w-0 flex-1 truncate text-start">
            {{ headerStageLabel }}
          </span>
          <span class="i-lucide-chevron-down size-3 flex-shrink-0" />
        </Button>
      </template>
      <template #content="{ hide }">
        <div v-if="pendingTerminal" class="w-64 p-3">
          <p class="mb-1 text-sm font-semibold text-n-slate-12">
            {{ pendingTerminal.emoji }} {{ pendingTerminal.name }}
          </p>
          <p class="mb-2 text-xs text-n-slate-10">
            {{ t('TELETEL_LIFECYCLE.CONTACT.TERMINAL_REASON_HINT') }}
          </p>
          <input
            v-model="reason"
            type="text"
            class="!mb-2 w-full"
            :placeholder="t('TELETEL_LIFECYCLE.CONTACT.TERMINAL_REASON_TITLE')"
            @keydown.enter.prevent="confirmTerminal(hide)"
          />
          <div class="flex flex-wrap gap-2">
            <Button
              size="xs"
              :label="t('TELETEL_LIFECYCLE.CONTACT.CONFIRM')"
              @click="confirmTerminal(hide)"
            />
            <Button
              size="xs"
              variant="faded"
              color="slate"
              :label="t('TELETEL_LIFECYCLE.CONTACT.SKIP')"
              @click="confirmTerminal(hide, false)"
            />
            <Button
              size="xs"
              variant="ghost"
              color="slate"
              :label="t('TELETEL_LIFECYCLE.CONTACT.CANCEL')"
              @click="cancelTerminal"
            />
          </div>
        </div>
        <div v-else class="w-64 p-2" role="menu">
          <p class="px-2 py-1 text-sm font-semibold text-n-slate-12">
            {{ t('TELETEL_LIFECYCLE.CONTACT.CHANGE_STAGE') }}
          </p>
          <p class="px-2 pb-2 text-xs text-n-slate-10">
            {{ t('TELETEL_LIFECYCLE.CONTACT.TITLE') }}
          </p>
          <button
            type="button"
            role="menuitemradio"
            :aria-checked="currentStageId === null"
            :disabled="isLoading || isTransitioning"
            class="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-start text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
            :class="{ 'bg-n-alpha-2 font-medium': currentStageId === null }"
            @click="selectHeaderStage(null, hide)"
          >
            <span
              class="flex size-5 flex-shrink-0 items-center justify-center rounded-md bg-n-alpha-2 text-xs"
              >—</span
            >
            <span class="min-w-0 flex-1 truncate">{{
              t('TELETEL_LIFECYCLE.UNCLASSIFIED')
            }}</span>
            <span
              v-if="currentStageId === null"
              class="i-lucide-check size-4 flex-shrink-0 text-n-brand"
            />
          </button>
          <div class="my-1 border-t border-n-weak" />
          <button
            v-for="stage in stages"
            :key="stage.id"
            type="button"
            role="menuitemradio"
            :aria-checked="String(stage.id) === String(currentStageId)"
            :disabled="isLoading || isTransitioning"
            class="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-start text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
            :class="{
              'bg-n-alpha-2 font-medium':
                String(stage.id) === String(currentStageId),
            }"
            @click="selectHeaderStage(stage.id, hide)"
          >
            <span
              class="flex size-5 flex-shrink-0 items-center justify-center text-base leading-none"
            >
              {{ stage.emoji || '•' }}
            </span>
            <span class="min-w-0 flex-1 truncate">{{ stage.name }}</span>
            <span
              v-if="String(stage.id) === String(currentStageId)"
              class="i-lucide-check size-4 flex-shrink-0 text-n-brand"
            />
          </button>
        </div>
      </template>
    </Popover>
  </div>
  <section
    v-else-if="isUsable"
    class="flex w-full flex-col gap-2 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
    :aria-label="t('TELETEL_LIFECYCLE.CONTACT.TITLE')"
  >
    <div class="flex min-w-0 items-center justify-between gap-2">
      <div class="min-w-0">
        <p class="mb-1 text-xs font-medium text-n-slate-10">
          {{ t('TELETEL_LIFECYCLE.CONTACT.TITLE') }}
        </p>
        <LifecycleStageBadge :stage="currentStage" :compact="compact" />
      </div>
      <button
        v-if="showHistory"
        type="button"
        class="inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium text-n-blue-11 hover:underline"
        :aria-expanded="historyOpen"
        @click="historyOpen = !historyOpen"
      >
        <span class="i-lucide-history size-3.5" />
        {{ t('TELETEL_LIFECYCLE.CONTACT.HISTORY') }}
      </button>
    </div>

    <div class="flex flex-col gap-1 text-xs text-n-slate-11">
      <label :for="`lifecycle-stage-${contactId}`">
        {{ t('TELETEL_LIFECYCLE.CONTACT.CHANGE_STAGE') }}
      </label>
      <div class="flex items-center gap-2">
        <select
          :id="`lifecycle-stage-${contactId}`"
          v-model="selectedStageId"
          class="h-9 min-w-0 flex-1 !mb-0 rounded-lg border border-n-strong bg-n-alpha-2 px-3 text-start text-sm text-n-slate-12 focus:border-n-brand"
          :disabled="isLoading || isTransitioning"
        >
          <option value="">{{ t('TELETEL_LIFECYCLE.UNCLASSIFIED') }}</option>
          <option v-for="stage in stages" :key="stage.id" :value="stage.id">
            {{ stage.emoji ? `${stage.emoji} ` : '' }}{{ stage.name }}
          </option>
        </select>
        <Button
          type="button"
          size="sm"
          :label="t('TELETEL_LIFECYCLE.CONTACT.APPLY')"
          :is-loading="isTransitioning"
          :disabled="isLoading || isTransitioning || !hasPendingStageChange"
          @click="applyStage"
        />
      </div>
      <input
        v-model="reason"
        type="text"
        maxlength="1000"
        class="h-9 !mb-0 rounded-lg border border-n-strong bg-n-alpha-2 px-3 text-sm text-n-slate-12"
        :placeholder="t('TELETEL_LIFECYCLE.CONTACT.REASON_PLACEHOLDER')"
        :aria-label="t('TELETEL_LIFECYCLE.CONTACT.REASON')"
        :disabled="isLoading || isTransitioning"
      />
      <p
        v-if="currentStageId === null && defaultStage"
        class="mb-0 text-xs leading-5 text-n-slate-10"
      >
        {{
          t('TELETEL_LIFECYCLE.CONTACT.DEFAULT_SUGGESTION', {
            name: defaultStage.name,
          })
        }}
      </p>
    </div>

    <p
      v-if="isTransitioning"
      class="mb-0 text-xs text-n-slate-10"
      aria-live="polite"
    >
      {{ t('TELETEL_LIFECYCLE.CONTACT.SAVING') }}
    </p>

    <ol
      v-if="historyOpen"
      class="m-0 flex list-none flex-col gap-2 border-t border-n-weak pt-2"
    >
      <li
        v-for="transition in historyState === 'loaded' ? transitions : []"
        :key="transition.id"
        class="flex items-start gap-2 text-xs"
      >
        <span class="mt-1.5 size-1.5 flex-shrink-0 rounded-full bg-n-brand" />
        <div class="min-w-0">
          <p class="mb-0 truncate text-n-slate-12">
            {{ stageSnapshotName(transition.from_stage) }}
            <span
              aria-hidden="true"
              class="i-lucide-arrow-right mx-1 inline-block size-3 rtl:-scale-x-100"
            />
            {{ stageSnapshotName(transition.to_stage) }}
          </p>
          <p class="mb-0 text-n-slate-10">
            {{ formatTime(transition.occurred_at) }}
            <span v-if="transition.actor?.name" class="ms-1">
              {{ transition.actor.name }}
            </span>
          </p>
          <p
            v-if="transition.reason"
            class="mb-0 whitespace-pre-wrap text-n-slate-11"
          >
            {{ transition.reason }}
          </p>
        </div>
      </li>
      <li
        v-if="historyState === 'loading'"
        class="text-xs text-n-slate-10"
        aria-live="polite"
      >
        {{ t('TELETEL_LIFECYCLE.CONTACT.HISTORY_LOADING') }}
      </li>
      <li
        v-else-if="historyState === 'error'"
        class="flex items-center justify-between gap-2 text-xs text-n-ruby-11"
        role="alert"
      >
        <span>{{ t('TELETEL_LIFECYCLE.CONTACT.HISTORY_ERROR') }}</span>
        <button
          type="button"
          class="flex-shrink-0 font-medium text-n-blue-11 hover:underline"
          :aria-label="t('TELETEL_LIFECYCLE.CONTACT.RETRY_HISTORY')"
          @click="loadInitialHistory"
        >
          {{ t('TELETEL_LIFECYCLE.CONTACT.RETRY') }}
        </button>
      </li>
      <li v-else-if="!transitions.length" class="text-xs text-n-slate-10">
        {{ t('TELETEL_LIFECYCLE.CONTACT.NO_HISTORY') }}
      </li>
      <li
        v-if="loadMoreHistoryFailed"
        class="flex items-center justify-between gap-2 pt-1 text-xs text-n-ruby-11"
        role="alert"
      >
        <span>{{ t('TELETEL_LIFECYCLE.CONTACT.LOAD_MORE_ERROR') }}</span>
        <button
          type="button"
          class="flex-shrink-0 font-medium text-n-blue-11 hover:underline"
          :aria-label="t('TELETEL_LIFECYCLE.CONTACT.RETRY_LOAD_MORE')"
          @click="loadMoreHistory"
        >
          {{ t('TELETEL_LIFECYCLE.CONTACT.RETRY') }}
        </button>
      </li>
      <li v-else-if="canLoadMoreHistory" class="pt-1 text-center">
        <button
          type="button"
          class="text-xs font-medium text-n-blue-11 hover:underline"
          :disabled="isLoadingMoreHistory"
          :aria-label="t('TELETEL_LIFECYCLE.CONTACT.LOAD_MORE')"
          @click="loadMoreHistory"
        >
          {{
            t(
              isLoadingMoreHistory
                ? 'TELETEL_LIFECYCLE.CONTACT.LOADING_HISTORY'
                : 'TELETEL_LIFECYCLE.CONTACT.LOAD_MORE'
            )
          }}
        </button>
      </li>
    </ol>
  </section>
</template>
